import React, { useMemo } from 'react';
import { App as AntApp, Typography, Space, Card, Divider } from 'antd';
import { getEnv } from './utils';
import {
  ProbesPanel as SharedProbesPanel,
  OverridesForm,
  AudienceSelector,
  ReadinessPanel,
  standardBackendProbes,
} from '@digiwedge/ui-health-panels';
import K8sSyncStatusPanel from './components/K8sSyncStatusPanel';
import SystemDetailsPanel from './components/SystemDetailsPanel';
import FullHealthDiagnosticsPanel from './components/FullHealthDiagnosticsPanel';
import IdpDiagnosticsPanel from './components/IdpDiagnosticsPanel';
import AuthDiagnosticsPanel from './components/AuthDiagnosticsPanel';
import LoginFlowDiagnosticsPanel from './components/LoginFlowDiagnosticsPanel';
import RouteAccessDiagnosticsPanel from './components/RouteAccessDiagnosticsPanel';

const { Title, Text } = Typography;

export default function AdminHealthPage(): React.ReactElement {
  AntApp.useApp();

  // Env defaults
  const envIdp = useMemo(() => getEnv('VITE_IDP_BASE_URL') || '', []);
  const envApi = useMemo(() => getEnv('VITE_ACCESS_CONTROL_BASE_URL') || '', []);

  type Overrides = { idpBase?: string; apiBase?: string };
  const overrides = useMemo<Overrides>(() => {
    try {
      const raw = localStorage.getItem('accesscontrol.admin.health.overrides');
      return raw ? (JSON.parse(raw) as Overrides) : {};
    } catch {
      return {};
    }
  }, []);
  const idpBase = useMemo(() => overrides.idpBase || envIdp, [overrides.idpBase, envIdp]);
  const apiBase = useMemo(() => overrides.apiBase || envApi, [overrides.apiBase, envApi]);

  let metaAudience = '';
  try {
    metaAudience =
      (
        document.querySelector('meta[name="idp-audience"]') as HTMLMetaElement | null
      )?.content?.trim() || '';
  } catch {
    /* ignore */
  }

  const defaultAudience = (() => {
    if (metaAudience) return metaAudience;
    const fromEnv = getEnv('VITE_IDP_AUDIENCE');
    return fromEnv || 'control-admin';
  })();
  // Audience handled by AudienceSelector

  return (
    <div style={{ padding: 16 }}>
      <Card style={{ marginBottom: 16 }}>
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Title level={3} style={{ margin: 0 }}>
            Access Control — System Health
          </Title>
          <Text type="secondary">
            Quick probes for IDP and API endpoints. Use overrides if running locally.
          </Text>
          <OverridesForm
            storageKey="accesscontrol.admin.health.overrides"
            env={{
              VITE_IDP_BASE_URL: envIdp,
              VITE_ACCESS_CONTROL_BASE_URL: envApi,
              idpBase,
              apiBase,
            }}
            fields={[
              {
                key: 'idpBase',
                label: 'Identity Provider base URL',
                placeholder: 'https://idp.example.com',
                effective: () => {
                  const v = idpBase?.trim();
                  if (!v || v === '/') return '(same-origin)';
                  return v;
                },
              },
              {
                key: 'apiBase',
                label: 'Access Control API base URL',
                placeholder: 'http://localhost:3345',
                effective: () => {
                  const v = apiBase?.trim();
                  if (!v || v === '/') return '(same-origin)';
                  return v;
                },
              },
            ]}
          />
          <div style={{ marginTop: 8 }}>
            <AudienceSelector
              options={[
                { value: 'control-admin', label: 'control-admin' },
                { value: 'teetime-admin', label: 'teetime-admin' },
                { value: 'teetime-web', label: 'teetime-web' },
              ]}
              defaultValue={defaultAudience}
            />
          </div>
          <Divider style={{ margin: '16px 0' }} />
        </Space>
      </Card>

      {/* Order: Probes → k8s Sync Status → System Details → Full Health → IDP Diagnostics → Auth/Capabilities */}
      {/* Shared, generic probes (basic endpoints) */}
      <SharedProbesPanel
        title="Core Probes"
        specs={[
          ...standardBackendProbes(apiBase).map((s) => ({ ...s, name: `Access Control ${s.name}` })),
          ...standardBackendProbes(idpBase).map((s) => ({ ...s, name: `Identity Provider ${s.name}` })),
        ]}
      />
      <K8sSyncStatusPanel apiBase={apiBase} />
      <SystemDetailsPanel apiBase={apiBase} />
      <FullHealthDiagnosticsPanel apiBase={apiBase} />
      <ReadinessPanel base={apiBase} />
      <IdpDiagnosticsPanel idpBase={idpBase} audience={metaAudience || 'control-admin'} />
      <LoginFlowDiagnosticsPanel />
      <AuthDiagnosticsPanel idpBase={idpBase} apiBase={apiBase} />
      <RouteAccessDiagnosticsPanel idpBase={idpBase} apiBase={apiBase} />
    </div>
  );
}
